import { faArrowLeft, faHome, faXmark } from '@fortawesome/pro-regular-svg-icons';
import IQLogoGlowAnimated from 'assets/IQLogos/IQAnimatedLogo/IQLogoAnimated';
import cx from 'classnames';

import ClickableIcon from 'lib/common/components/ClickableIcon';

import { IconSizeConstraints } from '../../../../components/Icon/Icon';
import { useAIContext } from '../../Context';
import SlideInEffect from '../common/SlideInEffect/SlideInEffect';
import { IQDisplayMode, TIQDisplayMode } from '../constants/IQDisplayMode';

import styles from './iq-modal-header.module.scss';

export default function IQModalHeader({ displayMode }: { displayMode: TIQDisplayMode }) {
  const {
    actions: { selectMode, closeIQModal, selectAction, resetIQApiState },
    state: { selectedAction, selectedMode },
  } = useAIContext();

  const isStandalone = displayMode === IQDisplayMode.STAND_ALONE;
  const backArrowFunction = () => {
    resetIQApiState();
    if (selectedMode) {
      selectMode(null);
      return;
    }

    selectAction(null);
  };

  const homeFunction = () => {
    resetIQApiState();
    selectMode(null);
    selectAction(null);
  };

  //Boolean() does not work because '' is false but Boolean('') is true
  const isLogoMinimized = (isStandalone && !!selectedAction) || !isStandalone;

  const shouldShowBackArrow = isStandalone && selectedAction;
  const shouldShowHomeIcon = isStandalone && selectedMode;

  const displayModeLower = displayMode.toLowerCase();

  /**
   * Kind of bit hacky, the IQ minimized logo needs to be rendered in header of the modal after a IQ action has been selected
   * however, during initial load of the IQ standalone, the logo needs to be rendered as a content and not as a header
   */
  if (isStandalone && !selectedAction) {
    return (
      <div className={cx(styles['iq-modal-header'], styles[`iq-modal-header--${displayModeLower}`])}>
        <div className={styles['iq-modal-header__cross-icon-container']}>
          <ClickableIcon icon={faXmark} size={17} onlyConstrain={IconSizeConstraints.HEIGHT} onClick={closeIQModal} />
        </div>
      </div>
    );
  }

  return (
    <div className={cx(styles['iq-modal-header'], styles[`iq-modal-header--${displayModeLower}`])}>
      <div className={styles['iq-modal-header__left']}>
        {isStandalone && (
          <>
            {shouldShowBackArrow && (
              <SlideInEffect transitionType="ease" travelDistance={30} key="back-arrow" direction="right">
                <ClickableIcon
                  icon={faArrowLeft}
                  size={17}
                  onlyConstrain={IconSizeConstraints.HEIGHT}
                  onClick={backArrowFunction}
                />
              </SlideInEffect>
            )}
            {shouldShowHomeIcon && (
              <SlideInEffect transitionType="ease" travelDistance={30} key="home-icon" direction="right">
                <ClickableIcon
                  icon={faHome}
                  size={17}
                  onlyConstrain={IconSizeConstraints.HEIGHT}
                  onClick={homeFunction}
                />
              </SlideInEffect>
            )}
          </>
        )}
      </div>

      <div className={styles['iq-modal-header__logo']}>
        <div
          className={cx(styles['iq-modal-header__logo__container'], {
            [styles['iq-modal-header__logo__container--minimized']]: isLogoMinimized,
          })}
        >
          <div
            className={cx(styles['iq-modal-header__logo__image'], {
              [styles['iq-modal-header__logo__image--minimized']]: isLogoMinimized,
            })}
            data-testid="iq-logo"
          >
            <IQLogoGlowAnimated aria-label="IQ Logo" isLogoMinimized={isLogoMinimized} />
          </div>
        </div>
      </div>

      <div className={styles['iq-modal-header__right']}>
        <ClickableIcon icon={faXmark} size={17} onlyConstrain={IconSizeConstraints.HEIGHT} onClick={closeIQModal} />
      </div>
    </div>
  );
}
